import React, { useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Image,
    Main,
    Page,
    PageContent,
    ResponsiveContext,
    TextInput,
} from 'grommet';
import { Key } from 'grommet-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API } from '../../api';
import UserService from '../../api/user-service';
import AppConstants from '../../helpers/AppConstants.js';
import PropTypes from 'prop-types';

export const ResetPassword = ({
    slugParam,
    dashboardPathTemplate,
    marketingImage,
    bannerLogo,
}) => {
    const params = useParams();
    const slug = slugParam ? params[slugParam] : null;
    const dashboardPath = slug
        ? (dashboardPathTemplate || '/dashboard').replace(':slug', slug)
        : (dashboardPathTemplate || '/dashboard');
    const size = React.useContext(ResponsiveContext);
    const isMobile = size === 'small';
    const [value, setValue] = useState({ password: '', passwordConfirmation: '' });
    const [buttonBusy, setButtonBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetCode = searchParams.get('code');

    const handleSubmit = async () => {
        setErrorMessage('');
        if (value.password !== value.passwordConfirmation) {
            setErrorMessage('Passwords do not match.');
            return;
        }
        if (!resetCode) {
            setErrorMessage('Invalid or expired reset link.');
            return;
        }
        setButtonBusy(true);
        try {
            const response = await API.post('/api/auth/reset-password', {
                code: resetCode,
                password: value.password,
                passwordConfirmation: value.passwordConfirmation,
            });

            const jwt = response.data?.jwt;
            const userId = response.data?.user?.id;
            if (!jwt || !userId) {
                setErrorMessage('Something went wrong. Please try again.');
                return;
            }
            localStorage.setItem(AppConstants.cacheKeys.authToken, jwt);
            localStorage.setItem(AppConstants.cacheKeys.userId, userId);

            const user = await new UserService().getUser(userId);
            if (!user) {
                localStorage.removeItem(AppConstants.cacheKeys.authToken);
                localStorage.removeItem(AppConstants.cacheKeys.userId);
                setErrorMessage('Something went wrong. Please try again.');
                return;
            }

            if (user.coach?.id) {
                localStorage.setItem(AppConstants.cacheKeys.coachId, user.coach.id);
            }

            navigate(dashboardPath);
        } catch (error) {
            console.log(error);
            setErrorMessage(
                'There was an error resetting your password. The link may have expired.',
            );
        }
        setButtonBusy(false);
    };

    const form = (
        <Form
            value={value}
            onChange={(nextValue) => setValue(nextValue)}
            onSubmit={handleSubmit}>
            <Box gap="10px">
                <FormField
                    name="password"
                    htmlFor="reset-password-input-id"
                    label="New Password">
                    <TextInput
                        name="password"
                        id="reset-password-input-id"
                        type="password"
                        icon={<Key />}
                    />
                </FormField>
                <FormField
                    name="passwordConfirmation"
                    htmlFor="reset-confirm-input-id"
                    label="Confirm New Password"
                    error={errorMessage}>
                    <TextInput
                        name="passwordConfirmation"
                        id="reset-confirm-input-id"
                        type="password"
                        icon={<Key />}
                    />
                </FormField>
                <Button
                    type="submit"
                    primary
                    fill="horizontal"
                    label="Set new password"
                    busy={buttonBusy}
                />
            </Box>
        </Form>
    );

    if (!marketingImage || !bannerLogo) {
        return (
            <Page>
                <PageContent>
                    <Box width="medium">{form}</Box>
                </PageContent>
            </Page>
        );
    }

    return (
        <Main>
            <Page kind="full" height="100vh" background="enrich-grey">
                <PageContent pad="none" height="100%" justify="center">
                    <Box direction="row">
                        {!isMobile && (
                            <Box>
                                <Image
                                    src={marketingImage}
                                    height="100%"
                                    fit="cover"
                                />
                            </Box>
                        )}
                        <Box
                            background="white"
                            round="15px"
                            width="100%"
                            pad="large"
                            margin="medium"
                            align="stretch"
                            justify="evenly"
                            gap="small">
                            <Image
                                src={bannerLogo}
                                width="200px"
                                alignSelf="center"
                            />
                            {form}
                        </Box>
                    </Box>
                </PageContent>
            </Page>
        </Main>
    );
};

ResetPassword.propTypes = {
    slugParam: PropTypes.string,
    dashboardPathTemplate: PropTypes.string,
    marketingImage: PropTypes.string,
    bannerLogo: PropTypes.string,
};
